import { lang } from '@umu/ukit-lang';
import * as React from 'react';
const { useCallback, useState } = React;
import { services } from 'common';
const { Toast } = services;
import { ImageCropEditor } from '../imageCropEditor';
import { ChooseImage, IChooseImageInfo } from '../chooseImage/chooseImage';
import { UploadLoading } from 'businessCommon/src/components/@uploadLoading/uploadLoading';
import { getUploadResult, IPayloadData } from 'businessCommon/src/services/uploader/uploader';

import { EMediaType } from '@umu/umu-types';
export type { IChooseImageInfo } from '../chooseImage/chooseImage';

interface IProps {
    teacherId: string; // 图片地址
    children: JSX.Element | string; // 上传按钮
    aspect?: number;
    cropClassName?: string;
    noUseAppUpload?: boolean; // 不使用 app 上传
    mediaType?: EMediaType;
    multiple?: boolean;
    onConfirm(data: IChooseImageInfo[]): void;
}

function encodeFile(file: File, teacherId: string, mediaType: EMediaType) {
    const result: Blob = file;
    const previewUrl = window.URL.createObjectURL(result);
    const fileExt = (file.name.split(/\./g).pop() || '').toLocaleLowerCase();

    return {
        blob: result,
        ext: fileExt,
        previewUrl,
        fileName: file.name,
        mediaType,
        teacherId,
    };
}

export const ImageCropUploader = (props: IProps) => {
    const { aspect, children, teacherId, noUseAppUpload, mediaType = EMediaType.IMAGE, multiple = false, cropClassName = '' } = props;
    const [image, setImage] = useState<IChooseImageInfo | null>(null);
    const [file, setFile] = useState<File | null>(null);

    const cancel = useCallback(() => {
        setFile(null);
        setImage(null);
    }, [props]);

    // app、微信走这个
    const onChoose = (urls: IChooseImageInfo[]) => {
        if (urls.length === 1) {
            setImage(urls[0]);
            return;
        }
        if (urls.length > 1) {
            props.onConfirm(urls);
        }
    };

    const onError = () => {
        Toast.show({ message: lang('图片上传失败，请重试') });
        cancel();
    };

    // 浏览器上传走这个
    const onBrowserSelectReady = (files: Array<{ file: File; previewUrl: string }>) => {
        if (!files.length) {
            return;
        }
        if (files.length === 1) {
            setFile(files[0].file);
            return;
        }

        if (files.length > 1) {
            UploadLoading.show({ message: lang('正在上传...') });
            Promise.all(files.map((item) => getUploadResult(encodeFile(item.file, teacherId, mediaType))))
                .then((payloadData) => {
                    props.onConfirm(
                        payloadData.map((item) => ({
                            id: '',
                            desc: '',
                            url: item.url,
                            file_size: item.fileSize,
                            resource_id: `${item.resourceId}`,
                        })),
                    );
                })
                .catch(onError)
                .finally(() => {
                    UploadLoading.hide();
                });
        }
    };

    const onCropConfirm = (data?: IPayloadData) => {
        if (data) {
            props.onConfirm([
                {
                    id: '',
                    desc: '',
                    url: data.url,
                    file_size: data.fileSize,
                    resource_id: `${data.resourceId}`,
                },
            ]);
        } else {
            image && props.onConfirm([image]);
        }
        cancel();
    };

    const renderCroper = () => {
        if (!image && !file) {
            return null;
        }
        return (
            <ImageCropEditor
                show
                aspect={aspect}
                teacherId={teacherId}
                mediaType={mediaType}
                input={file || image!.url}
                className={cropClassName}
                onCancel={cancel}
                onConfirm={onCropConfirm}
            />
        );
    };

    return (
        <>
            <ChooseImage
                onlyPreview
                multiple={multiple}
                teacherId={teacherId}
                noUseAppUpload={noUseAppUpload}
                onError={onError}
                onChoose={onChoose}
                onBrowserSelectReady={onBrowserSelectReady}
            >
                {children}
            </ChooseImage>
            {renderCroper()}
        </>
    );
};
